import { api, type InfoShareGateResponse, ApiError } from '../api/client';
import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import {
  decryptManifest,
  deriveProofKeys,
  resolveContentKey,
  resolveFragmentForToken,
  signGateChallenge,
} from '../crypto/infoShareFragment';
import { decodeBase64Url } from '../crypto/primitives';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { mountGuestReplyForm, guestReplyAllowsAnother } from '../ui/guestReplyForm';
import { renderGuestInfoShareAttachments } from '../ui/guestInfoShareAttachments';
import { mountSmsPasswordInput } from '../crypto/smsPassword';
import { iconImg } from '../ui/icons';
import { createHelpTip } from '../help/helpTips';
import {
  startGuestAccessExpiryMonitor,
  type GuestAccessExpiryHandle,
} from '../app/guestAccessExpiry';
import { logoutGuest } from '../app/index';

function getGuestToken(): string | null {
  const match = window.location.pathname.match(/^\/i\/([^/]+)$/);
  return match?.[1] ?? null;
}

async function verifyGuestSession(
  token: string,
  proofSecretKey: Uint8Array,
  gateResponseText: string,
  password?: string,
): Promise<void> {
  const gate = JSON.parse(gateResponseText) as { nonce: string };
  if (!gate.nonce) {
    throw new Error('missing_gate_nonce');
  }

  const signature = await signGateChallenge(proofSecretKey, gate.nonce, gateResponseText);
  await api.verifyInfoShare(token, signature, password);
}

async function refreshGuestGate(token: string): Promise<{
  gateResponseText: string;
  gate: InfoShareGateResponse;
}> {
  const gateResponseText = await api.getInfoShareGate(token);
  const gate = JSON.parse(gateResponseText) as InfoShareGateResponse;
  return { gateResponseText, gate };
}

export function GuestInfoSharePage(): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'center-panel guest-panel';

  const token = getGuestToken();
  if (!token) {
    panel.innerHTML = `<p class="error-text" data-i18n="info.guest.invalidLink"></p>`;
    applyI18n(panel);
    return panel;
  }

  const fragment = resolveFragmentForToken(token);
  if (!fragment) {
    panel.innerHTML = `
      <h1 data-i18n="info.guest.missingFragmentTitle"></h1>
      <p class="muted" data-i18n="info.guest.missingFragment"></p>
    `;
    applyI18n(panel);
    return panel;
  }

  const contentKeyFromFragment = fragment.ck ? decodeBase64Url(fragment.ck) : null;
  const proofSeed = decodeBase64Url(fragment.fs);
  const { secretKey: proofSecretKey } = deriveProofKeys(proofSeed);
  const passwordProtected = Boolean(fragment.salt);

  panel.innerHTML = `
    <h1 class="panel-title" id="guest-title">
      <span class="panel-title__icon"></span>
      <span data-i18n="info.guest.title"></span>
    </h1>
    <div class="warning-panel" id="guest-access-warning" hidden>
      <span class="warning-panel__icon"></span>
      <span id="guest-access-warning-text" data-i18n="info.guest.multiUseWarning"></span>
    </div>
    <label class="guest-password-field" id="guest-password-field" hidden>
      <span data-i18n="info.guest.passwordLabel"></span>
      <input type="password" id="guest-password" autocomplete="off" />
    </label>
    <div class="guest-password-field" id="guest-sms-password-field" hidden></div>
    <div id="guest-actions">
      <button type="button" class="button primary" id="guest-unseal-button" disabled>
        <span class="button__icon"></span>
        <span id="guest-unseal-label" data-i18n="info.guest.unseal"></span>
      </button>
    </div>
    <article class="module-section guest-message-card" id="guest-message" hidden>
      <h2 data-i18n="info.guest.messageTitle"></h2>
      <pre class="message-display message-surface" id="guest-message-text"></pre>
      <div id="guest-message-attachments" hidden></div>
    </article>
    <div id="guest-reply-host"></div>
    <p class="error-text" id="guest-error" hidden></p>
    <p class="muted" id="guest-status" hidden></p>
  `;

  panel.querySelector('.panel-title__icon')?.append(iconImg('guest', 28));
  panel.querySelector('#guest-title')?.append(createHelpTip('guest-information'));
  panel.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  panel.querySelector('.button__icon')?.append(iconImg('unlock', 18));

  const actions = panel.querySelector('#guest-actions') as HTMLDivElement;
  const unsealButton = panel.querySelector('#guest-unseal-button') as HTMLButtonElement;
  const unsealLabel = panel.querySelector('#guest-unseal-label') as HTMLSpanElement;
  const messageCard = panel.querySelector('#guest-message') as HTMLElement;
  const messageText = panel.querySelector('#guest-message-text') as HTMLPreElement;
  const messageAttachments = panel.querySelector('#guest-message-attachments') as HTMLDivElement;
  const replyHost = panel.querySelector('#guest-reply-host') as HTMLDivElement;
  const error = panel.querySelector('#guest-error') as HTMLParagraphElement;
  const status = panel.querySelector('#guest-status') as HTMLParagraphElement;
  const accessWarning = panel.querySelector('#guest-access-warning') as HTMLDivElement;
  const accessWarningText = panel.querySelector('#guest-access-warning-text') as HTMLSpanElement;
  const passwordField = panel.querySelector('#guest-password-field') as HTMLLabelElement;
  const passwordInput = panel.querySelector('#guest-password') as HTMLInputElement;
  const smsPasswordField = panel.querySelector('#guest-sms-password-field') as HTMLDivElement;
  const smsPasswordInput = mountSmsPasswordInput(smsPasswordField);
  applyI18n(smsPasswordField);

  let gateResponseText = '';
  let gateReady = false;
  let replyOnlyMode = false;
  let gateInfo: InfoShareGateResponse | null = null;
  let replyFormMounted = false;
  let passwordRequired = passwordProtected;
  let smsPasswordMode = false;

  const hidePasswordFields = () => {
    passwordField.hidden = true;
    smsPasswordInput.setVisible(false);
  };

  let statusKey: string | null = null;
  let errorKey: string | null = null;
  let accessWarningKey: string | null = null;
  let expiryMonitor: GuestAccessExpiryHandle | null = null;

  const showExpiryWarning = (remainingMs: number) => {
    const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
    accessWarning.hidden = false;
    accessWarningText.removeAttribute('data-i18n');
    accessWarningText.textContent = t('info.guest.expiresSoon', { minutes });
  };

  const clearExpiryWarning = () => {
    if (accessWarningKey) {
      accessWarning.hidden = false;
      accessWarningText.removeAttribute('data-i18n');
      accessWarningText.textContent = t(accessWarningKey);
      return;
    }
    accessWarning.hidden = true;
    accessWarningText.textContent = '';
  };

  const startExpiryMonitor = (expiresAt: string | null | undefined) => {
    expiryMonitor?.dispose();
    expiryMonitor = null;
    if (!expiresAt) return;
    expiryMonitor = startGuestAccessExpiryMonitor({
      expiresAt,
      refreshExpiry: async () => {
        const refreshed = await refreshGuestGate(token);
        gateInfo = refreshed.gate;
        return {
          expired: refreshed.gate.expired,
          revoked: refreshed.gate.revoked,
          expiresAt: refreshed.gate.expiresAt,
        };
      },
      onWarning: showExpiryWarning,
      onWarningClear: clearExpiryWarning,
      onExpiredLogout: async () => {
        await logoutGuest();
        if (!panel.isConnected) return;
        messageCard.hidden = true;
        actions.hidden = true;
        replyHost.replaceChildren();
        accessWarning.hidden = true;
        statusKey = 'info.guest.expired';
        status.hidden = false;
        status.textContent = t(statusKey);
      },
    });
  };
  let unsealLabelKey: string | null = null;

  const refreshLocale = () => {
    if (accessWarningKey && !accessWarning.hidden) {
      accessWarningText.textContent = t(accessWarningKey);
    }

    if (unsealLabelKey) {
      unsealLabel.textContent = t(unsealLabelKey);
    } else if (unsealLabel.hasAttribute('data-i18n')) {
      applyI18n(unsealLabel);
    }

    if (statusKey && !status.hidden) {
      status.textContent = t(statusKey);
    }

    if (errorKey && !error.hidden) {
      error.textContent = t(errorKey);
    }
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const guestToken = token;

  function showReplyForm(): void {
    if (replyFormMounted || !gateInfo?.allowGuestReply) {
      return;
    }

    mountGuestReplyForm(
      replyHost,
      guestToken,
      gateInfo.replyAllowsFiles,
      gateInfo.remainingReplyFiles,
      gateInfo.remainingReplyBytes,
      guestReplyAllowsAnother(gateInfo.allowGuestReply),
    );
    replyFormMounted = true;
  }

  void (async () => {
    try {
      const refreshed = await refreshGuestGate(guestToken);
      gateResponseText = refreshed.gateResponseText;
      gateInfo = refreshed.gate;
      const gate = refreshed.gate;

      if (gate.expired || gate.revoked) {
        accessWarning.hidden = true;
        accessWarningKey = null;
        actions.hidden = true;
        statusKey = gate.revoked ? 'info.guest.revoked' : 'info.guest.expired';
        status.hidden = false;
        status.textContent = t(statusKey);
        return;
      }

      if (gate.sealed && !gate.multiUseAccess && !gate.allowGuestReply) {
        accessWarning.hidden = true;
        accessWarningKey = null;
        actions.hidden = true;
        statusKey = 'info.guest.alreadyOpened';
        status.hidden = false;
        status.textContent = t(statusKey);
        return;
      }

      if (gate.sealed && !gate.multiUseAccess && gate.allowGuestReply) {
        replyOnlyMode = true;
        accessWarning.hidden = true;
        accessWarningKey = null;
        statusKey = 'info.guest.alreadyOpenedReply';
        status.hidden = false;
        status.textContent = t(statusKey);
        unsealLabelKey = 'info.guest.continueToReply';
        unsealLabel.removeAttribute('data-i18n');
        unsealLabel.textContent = t(unsealLabelKey);
        gateReady = true;
        unsealButton.disabled = false;
        return;
      }

      accessWarningKey = gate.multiUseAccess ? 'info.guest.multiUseWarning' : null;
      if (accessWarningKey) {
        accessWarning.hidden = false;
        accessWarningText.removeAttribute('data-i18n');
        accessWarningText.textContent = t(accessWarningKey);
      } else {
        accessWarning.hidden = true;
      }

      if (gate.passwordRequired) {
        passwordRequired = true;
        smsPasswordMode = gate.passwordMode === 'Sms';
        if (smsPasswordMode) {
          smsPasswordInput.setVisible(true);
          smsPasswordInput.focus();
        } else {
          passwordField.hidden = false;
          passwordInput.required = true;
        }
      }

      if (!contentKeyFromFragment && !passwordRequired && !replyOnlyMode) {
        accessWarning.hidden = true;
        accessWarningKey = null;
        actions.hidden = true;
        errorKey = 'info.guest.invalidLink';
        error.hidden = false;
        error.textContent = t(errorKey);
        return;
      }

      gateReady = true;
      unsealButton.disabled = false;
    } catch (cause) {
      console.error('Guest gate failed', cause);
      accessWarning.hidden = true;
      accessWarningKey = null;
      actions.hidden = true;
      errorKey = 'info.guest.invalidLink';
      error.hidden = false;
      error.textContent = t(errorKey);
    }
  })();

  unsealButton.addEventListener('click', () => {
    void (async () => {
      if (!gateReady) {
        return;
      }

      error.hidden = true;

      setCryptoBusy(panel, actions, true, {
        titleKey: replyOnlyMode ? 'info.guest.replyUnlocking' : 'info.guest.unsealing',
        detailKey: replyOnlyMode ? 'info.guest.replyUnlockingDetail' : 'info.guest.unsealingDetail',
        icon: 'hourglass',
      });

      try {
        const refreshed = await refreshGuestGate(guestToken);
        gateResponseText = refreshed.gateResponseText;
        gateInfo = refreshed.gate;

        if (refreshed.gate.expired || refreshed.gate.revoked) {
          accessWarning.hidden = true;
          accessWarningKey = null;
          actions.hidden = true;
          hidePasswordFields();
          statusKey = refreshed.gate.revoked ? 'info.guest.revoked' : 'info.guest.expired';
          status.hidden = false;
          status.textContent = t(statusKey);
          return;
        }

        if (refreshed.gate.sealed && !refreshed.gate.multiUseAccess && !refreshed.gate.allowGuestReply) {
          accessWarning.hidden = true;
          accessWarningKey = null;
          actions.hidden = true;
          hidePasswordFields();
          statusKey = 'info.guest.alreadyOpened';
          status.hidden = false;
          status.textContent = t(statusKey);
          return;
        }

        if (refreshed.gate.sealed && !refreshed.gate.multiUseAccess && refreshed.gate.allowGuestReply) {
          replyOnlyMode = true;
          accessWarning.hidden = true;
          accessWarningKey = null;
          statusKey = 'info.guest.alreadyOpenedReply';
          status.hidden = false;
          status.textContent = t(statusKey);
          unsealLabelKey = 'info.guest.continueToReply';
          unsealLabel.removeAttribute('data-i18n');
          unsealLabel.textContent = t(unsealLabelKey);
        }

        const password = passwordRequired
          ? (smsPasswordMode ? smsPasswordInput.getPassword() : passwordInput.value)
          : undefined;
        await verifyGuestSession(guestToken, proofSecretKey, gateResponseText, password);

        if (replyOnlyMode) {
          actions.hidden = true;
          status.hidden = true;
          hidePasswordFields();
          showReplyForm();
          return;
        }

        const contentKey = await resolveContentKey(fragment, password);
        if (!contentKey) {
          throw new Error('missing_content_key');
        }

        const opened = await api.openInfoShare(guestToken);
        if (!opened.opened && opened.alreadyOpened && !opened.multiUseAccess) {
          if (gateInfo?.allowGuestReply) {
            replyOnlyMode = true;
            actions.hidden = true;
            statusKey = 'info.guest.alreadyOpenedReply';
            status.hidden = false;
            status.textContent = t(statusKey);
            hidePasswordFields();
            showReplyForm();
            return;
          }

          accessWarning.hidden = true;
          accessWarningKey = null;
          actions.hidden = true;
          hidePasswordFields();
          statusKey = 'info.guest.alreadyOpened';
          status.hidden = false;
          status.textContent = t(statusKey);
          return;
        }

        const manifestBytes = await api.fetchInfoShareManifest(guestToken);
        const manifest = await decryptManifest(contentKey, manifestBytes);
        messageText.textContent = manifest.text;
        await renderGuestInfoShareAttachments({
          container: messageAttachments,
          attachments: manifest.attachments,
          contentKey,
          token: guestToken,
          multiUseAccess: Boolean(gateInfo?.multiUseAccess ?? opened.multiUseAccess),
          onError: (messageKey) => {
            error.hidden = false;
            errorKey = messageKey;
            error.textContent = t(messageKey);
          },
        });
        messageCard.hidden = false;
        actions.hidden = true;
        accessWarning.hidden = true;
        accessWarningKey = null;
        hidePasswordFields();

        const isSingleUse = !(gateInfo?.multiUseAccess ?? opened.multiUseAccess);
        if (isSingleUse) {
          statusKey = 'info.guest.singleUseOpenedHint';
          status.hidden = false;
          status.textContent = t(statusKey);
        }

        if (gateInfo?.allowGuestReply) {
          showReplyForm();
        }

        startExpiryMonitor(gateInfo?.expiresAt);
      } catch (cause) {
        console.error('Guest unseal failed', cause);
        error.hidden = false;
        if (
          cause instanceof ApiError
          && (cause.code === 'verify_locked_out' || cause.code === 'rate_limited')
        ) {
          errorKey = cause.code === 'verify_locked_out'
            ? 'errors.verifyLockedOut'
            : 'errors.rateLimited';
          error.textContent = apiErrorMessage(cause);
        } else {
          const incorrectPassword =
            cause instanceof ApiError && cause.code === 'incorrect_password';
          const nonceExpired =
            cause instanceof ApiError && cause.code === 'nonce_expired';
          errorKey = incorrectPassword
            ? 'info.guest.incorrectPassword'
            : nonceExpired
              ? 'info.guest.nonceExpired'
              : 'info.guest.unsealFailed';
          error.textContent = t(errorKey);
        }
      } finally {
        setCryptoBusy(panel, actions, false, {
          titleKey: replyOnlyMode ? 'info.guest.replyUnlocking' : 'info.guest.unsealing',
          detailKey: replyOnlyMode ? 'info.guest.replyUnlockingDetail' : 'info.guest.unsealingDetail',
        });
      }
    })();
  });

  const observer = new MutationObserver(() => {
    if (!panel.isConnected) {
      expiryMonitor?.dispose();
      expiryMonitor = null;
      unbindLocale();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  applyI18n(panel);
  return panel;
}
